import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { getUser } from '../../utils/Common';
import Layout from '../../components/Layout';
import DataTable from 'react-data-table-component';
import '../../styles/datatable.css';

function ItemsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [hotelFilter, setHotelFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  
  const [hotels, setHotels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modalImage, setModalImage] = useState(null);

  // Fetch hotels and departments list
  useEffect(() => {
    const userData = getUser() || {};
    const hcode = userData.hotel_id;
    
    axios.post('/list/hotel/by-user', { hcode })
      .then(res => setHotels(res.data || []))
      .catch(err => console.error('Error fetching hotels:', err));
      
    axios.get('/list/departments')
      .then(res => setDepartments(res.data || []))
      .catch(err => console.error('Error fetching departments:', err));
  }, []);

  // Fetch items when filters change (also runs on initial mount)
  useEffect(() => {
    const fetchItems = () => {
      setLoading(true);

      axios.post('/list/item', {
        hotel_filter: hotelFilter,
        dept_filter: deptFilter
      })
        .then(res => {

          if (res.data && res.data.status === 200) {
            const itemsArray = res.data.data;

            setItems(Array.isArray(itemsArray) ? itemsArray : []);
          } else {

            setItems([]);
          }
          setLoading(false);
        })
        .catch(err => {


          setItems([]);
          setLoading(false);
        });
    };
    
    fetchItems();
  }, [hotelFilter, deptFilter]);

  const getImageUrl = (imgPath) => {
    if (!imgPath) return null;
    if (imgPath.startsWith('http')) return imgPath;
    return `http://localhost:8001${imgPath}`;
  };

  // Custom styles for DataTable
  const customStyles = {
    table: {
      style: {
        backgroundColor: 'white',
        borderRadius: '12px',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f3f4f6',
        borderBottomWidth: '2px',
        borderBottomColor: '#e5e7eb',
        minHeight: '40px',
      },
    },
    headCells: {
      style: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#374151',
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    rows: {
      style: {
        fontSize: '12px',
        minHeight: '40px',
        '&:hover': {
          backgroundColor: '#f9fafb',
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
      },
    },
  };

  // DataTable columns
  const columns = [
    {
      name: 'Image',
      width: '80px',
      cell: row => (
        <div className="flex items-center">
          {getImageUrl(row.img_item) ? (
            <img 
              src={getImageUrl(row.img_item)} 
              alt={row.nama_item}
              onClick={() => setModalImage(getImageUrl(row.img_item))}
              className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-75"
            />
          ) : (
            <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-gray-400 text-xs">No img</span>
            </div>
          )}
        </div>
      ),
    },
    {
      name: 'Item Name',
      selector: row => row.nama_item,
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: 'Unit',
      selector: row => row.unit,
      sortable: true,
      width: '120px',
      wrap: true,
    },
    {
      name: 'Min Order',
      selector: row => row.min_order,
      sortable: true,
      width: '120px',
      wrap: true,
    },
    {
      name: 'Hotel',
      selector: row => row.nm_hotel,
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: 'Department',
      selector: row => row.nm_department,
      sortable: true,
      grow: 2,
      wrap: true,
    },
  ];

  // Filter items based on search text
  const filteredItems = items.filter(item => 
    item.nama_item?.toLowerCase().includes(filterText.toLowerCase()) ||
    item.nm_hotel?.toLowerCase().includes(filterText.toLowerCase()) ||
    item.nm_department?.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <Layout title="Items Catalog" subtitle="All items by hotel">
      <div className="p-4 sm:p-6 md:p-8">
        
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Items Catalog</h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage and filter items across all hotels and departments
              </p>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
          
          {/* Filters */}
          <div className="filter-scroll-mobile gap-4 mb-4 pb-2">
            <div className="filter-item-nowrap relative min-w-[200px]">
              <input
                type="text"
                placeholder="Search items..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={hotelFilter}
              onChange={(e) => setHotelFilter(e.target.value)}
              className="filter-item-nowrap px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Hotels</option>
              {hotels.map(hotel => (
                <option key={hotel.id_hotel} value={hotel.id_hotel}>{hotel.nm_hotel}</option>
              ))}
            </select>
            
            <select
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="filter-item-nowrap px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id_department} value={dept.id_department}>{dept.nm_department}</option>
              ))}
            </select>
            
            <button
              onClick={() => {
                setFilterText(''); 
                setHotelFilter('');
                setDeptFilter('');
              }}
              className="filter-item-nowrap px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* DataTable */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredItems}
            pagination
            paginationPerPage={20}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            }
            noDataComponent={
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg mb-2">No items found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            }
            highlightOnHover
            pointerOnHover
            responsive
            dense
          />
        </div>
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setModalImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setModalImage(null)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
            >
              ✕
            </button>
            <img 
              src={modalImage} 
              alt="Item" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </Layout>
  );
}

export default ItemsList;
